/**
 * Dex live-scan "hold steady" prompt — Round-12 / 2026-06-25 (Fix 3).
 *
 * The PiP HUD already has a HOLD / "Scanning…" coaching state, but it never
 * actively tells the trainer to SLOW DOWN. Two distinct failure modes during a
 * purified single-pass scan both manifest as "numbers are being read but few
 * confirm":
 *
 *   - HIGH MOTION / BLUR: the camera is moving fast enough that frames are smeared
 *     or shifting between rows (the blur / scroll-stability gates are dropping
 *     them), so no stable grid snapshot ever corroborates.
 *   - LOW SIGHTING ACCUMULATION: the grid is readable but the trainer scrolls so
 *     fast that each dexId is seen only once or twice before leaving the screen —
 *     it never reaches the 2 positives the purified ratio gate needs.
 *
 * This is a PURE, framework-agnostic debounced state machine: given a per-frame
 * (motionHigh, lowCapture) reading it decides whether to SHOW the hold-steady
 * prompt, debounced so a single noisy frame never flickers the HUD and the prompt
 * lingers briefly after the condition clears (so it does not nag on/off). It owns
 * NO copy and NO telemetry side effects — the caller renders the message and emits
 * `[dex-scan] hold steady prompt {reason, shown}`. It NEVER touches any write,
 * gate, veto, or confirmation decision: it is guidance only.
 */

/** Which signal is currently driving the hold-steady prompt. */
export type HoldSteadyReason = "motion" | "low_capture" | "both";

export interface HoldSteadyInputs {
  /** This frame shows high motion / blur (a blur or scroll-stability drop). */
  motionHigh: boolean;
  /** Numbers are being read but few are reaching confirmation (scrolling too fast). */
  lowCapture: boolean;
}

export interface HoldSteadyDecision {
  /** True while the debounced prompt should be visible. */
  shown: boolean;
  /** The reason the prompt is shown, or null when not shown. */
  reason: HoldSteadyReason | null;
  /**
   * True when `shown` and/or `reason` changed on THIS observation — the caller's
   * cue to emit the `[dex-scan] hold steady prompt` telemetry exactly once per
   * transition instead of every frame.
   */
  changed: boolean;
}

export interface HoldSteadyConfig {
  /**
   * Consecutive frames the active condition must persist before the prompt is
   * SHOWN. Debounces a single noisy frame from flashing the HUD.
   */
  persistFrames: number;
  /**
   * Consecutive clear frames required before a shown prompt is HIDDEN. Holds the
   * message briefly after the condition clears so it does not flicker on/off.
   */
  clearFrames: number;
  /**
   * Round-13 Fix B — HARD auto-expire cap. A shown prompt is force-hidden once it
   * has been visible for this many frames, EVEN IF the active condition is still
   * reading true. Without this a permanently-true input (e.g. a wedged
   * lowCapture/motion signal) pins the prompt on for the rest of the session —
   * device traces showed it stuck for ~15-20s. After the cap fires the detector
   * fully resets, so the prompt can re-trigger later if the problem is real and
   * persists again. Optional; defaults to DEFAULT_HOLD_STEADY_CONFIG.maxShownFrames.
   */
  maxShownFrames?: number;
}

/**
 * Defaults tuned for the ~1 Hz broadcast cadence: require ~2 frames (~2s) of a
 * sustained problem before prompting, and hold the prompt ~3 frames (~3s) after
 * it clears so a brief good patch does not blink it off.
 */
export const DEFAULT_HOLD_STEADY_CONFIG: HoldSteadyConfig = {
  persistFrames: 2,
  clearFrames: 3,
  // ~10 frames ≈ ~10s at the ~1 Hz cadence — long enough to be a genuine, useful
  // nudge, short enough that a wedged signal can never pin the prompt for the rest
  // of the session (the observed ~15-20s stuck-prompt bug).
  maxShownFrames: 10,
};

/**
 * Round-13 Fix B — decide the per-frame `lowCapture` signal with a SCROLL-MOTION
 * gate. "Numbers are read but few confirm" is only worth a SLOW-DOWN nudge while
 * the trainer is actually SCROLLING — if the view is stationary (no new dexId has
 * entered the grid) the same read/no-commit pattern just means a hard-to-confirm
 * cell sitting still, and telling a stationary trainer to "scroll slower" is
 * nonsense (and was pinning the prompt on). So lowCapture fires ONLY when the
 * read-without-apply streak has reached its threshold AND the grid advanced this
 * frame. Pure; the caller supplies both the streak and the scroll-advanced bit.
 */
export function isLowCaptureActive(args: {
  /** Consecutive frames where numbers were read but nothing was applied. */
  readWithoutApplyStreak: number;
  /** Frames of read-without-apply required before lowCapture can fire. */
  threshold: number;
  /** True when the grid advanced this frame (a new dexId entered = scrolling). */
  scrollAdvanced: boolean;
}): boolean {
  return args.scrollAdvanced && args.readWithoutApplyStreak >= args.threshold;
}

function reasonOf(inputs: HoldSteadyInputs): HoldSteadyReason | null {
  if (inputs.motionHigh && inputs.lowCapture) return "both";
  if (inputs.motionHigh) return "motion";
  if (inputs.lowCapture) return "low_capture";
  return null;
}

/**
 * Create a debounced hold-steady prompt detector for one scan session. Pure
 * bookkeeping — call `observe` once per processed frame.
 */
export function createHoldSteadyDetector(config: HoldSteadyConfig = DEFAULT_HOLD_STEADY_CONFIG) {
  const persistFrames = Math.max(1, config.persistFrames);
  const clearFrames = Math.max(1, config.clearFrames);
  const maxShownFrames = Math.max(
    1,
    config.maxShownFrames ?? DEFAULT_HOLD_STEADY_CONFIG.maxShownFrames ?? 10,
  );

  let shown = false;
  let reason: HoldSteadyReason | null = null;
  let activeStreak = 0; // consecutive frames the condition has been active
  let clearStreak = 0; // consecutive clear frames while shown
  let shownFrames = 0; // consecutive frames the prompt has been visible (auto-expire)

  return {
    observe(inputs: HoldSteadyInputs): HoldSteadyDecision {
      const active = inputs.motionHigh || inputs.lowCapture;
      const frameReason = reasonOf(inputs);

      if (active) {
        activeStreak += 1;
        clearStreak = 0;
      } else {
        clearStreak += 1;
        activeStreak = 0;
      }

      const prevShown = shown;
      const prevReason = reason;

      if (!shown) {
        // Show only after the condition has persisted long enough to be real.
        if (active && activeStreak >= persistFrames) {
          shown = true;
          reason = frameReason;
          shownFrames = 1;
        }
      } else {
        shownFrames += 1;
        // Round-13 Fix B — HARD auto-expire. Once visible for the full cap, force
        // the prompt off and fully reset (streaks + counter) even if the input is
        // still active, so a wedged signal can never pin it on indefinitely. A
        // genuinely persistent problem re-triggers cleanly on a later frame.
        if (shownFrames >= maxShownFrames) {
          shown = false;
          reason = null;
          activeStreak = 0;
          clearStreak = 0;
          shownFrames = 0;
        } else if (active) {
          // Refresh the reason while still active.
          reason = frameReason;
        } else if (clearStreak >= clearFrames) {
          // Hide only after the condition has been clear for the full hold window.
          shown = false;
          reason = null;
          shownFrames = 0;
        }
      }

      const changed = shown !== prevShown || reason !== prevReason;
      return { shown, reason, changed };
    },

    /** Reset between scan sessions. */
    reset(): void {
      shown = false;
      reason = null;
      activeStreak = 0;
      clearStreak = 0;
      shownFrames = 0;
    },

    /** Current debounced state without observing a new frame. */
    peek(): HoldSteadyDecision {
      return { shown, reason, changed: false };
    },
  };
}

export type HoldSteadyDetector = ReturnType<typeof createHoldSteadyDetector>;
